import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { cellText, parseCsv } from '../people/excel.js';
import { LEDGER_EXPORT_ENTITIES } from '@lg/d365-client';
import { manifestEntityXml, toCsv } from './dataPackage.js';

/**
 * A data package exported from the template company (Data management → Export, format Package),
 * turned into an import package for a new company. Entities that export every company (Ledger,
 * fiscal periods, number sequences, main-account overrides) are cut to the template company's rows,
 * so other companies are never re-imported; company columns that hold the template company's ID
 * get the new ID. Manifest.xml, PackageHeader.xml, file names, encodings and every other value
 * stay as exported. Cells elsewhere that still mention the template company (a number
 * sequence code like 1100_GJ, say) are reported, not changed: those are decisions, not copies.
 */

/** Staging columns that carry the company a row belongs to (upper case). VERIFY against exports. */
export const COMPANY_COLUMNS: ReadonlySet<string> = new Set([
  'DATAAREAID',
  'DATAAREA',
  'COMPANY',
  'COMPANYID',
  'LEGALENTITY',
  'LEGALENTITYID',
  'LEGALENTITYDATAAREAID',
  // A company's ledger is normally named after it (fiscal period status rows name the ledger).
  'LEDGER',
  'LEDGERNAME',
  // Seen in the ledger export (2026-10-22): number sequence scope, journal control, journal names
  // and main-account overrides name the company in these. Only exact matches are rewritten.
  'SCOPEVALUE',
  'JOURNALLEGALENTITY',
  'LEDGERLEGALENTITY',
  'VOUCHERSERIESCOMPANYID',
  'TAXTABLELEGALENTITY',
  'ITEMSALESTAXGROUPHEADINGLEGALENTITY',
]);

/**
 * Columns that are a company only in one entity (lower-case entity name → columns). The ledger's
 * NAME is the company ID and must be unique, so a copied "1100" ledger would collide with 1100's.
 */
export const ENTITY_COMPANY_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  ledger: ['NAME'],
};

/**
 * References that point at the template company's own records and can't carry over (lower-case
 * entity name → columns): cleared and reported. Journal names link workflows by ID, and a workflow
 * gets a new ID when it's imported into the new company; relink after the workflow import.
 */
export const ENTITY_CLEAR_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  'journal names': ['WORKFLOWID'],
};

/**
 * Values that embed the template company ID and should carry the new one (lower-case entity name →
 * columns): number sequences named and formatted after the company (1100-######, EBRI-1100) and
 * everything that points at those codes, so the renamed codes stay linked. Whole tokens only.
 */
export const ENTITY_RENAME_COLUMNS: Readonly<Record<string, readonly string[]>> = {
  'number sequence code v2': ['NUMBERSEQUENCECODE', 'NAME', 'FORMAT', 'ANNOTATEDFORMAT'],
  'number sequence references v2': ['NUMBERSEQUENCECODE'],
  'journal names': ['VOUCHERSERIESCODE'],
};

/**
 * Composite scope values join their parts with ";$;", company first: a number sequence scoped to a
 * company's fiscal period has SCOPEVALUE "USMF;$;Fiscal;$;2014;$;Opening". The company is that
 * first part, and only that part is rewritten.
 */
const SCOPE_SEPARATOR = ';$;';
const companyPart = (value: string): string => value.split(SCOPE_SEPARATOR)[0]?.trim() ?? '';

/** Record IDs are internal to the source company's rows (LEDGERRECID, …): cleared, never copied. */
const isRecIdColumn = (col: string): boolean => /RECID$/.test(col);

export interface RetargetEntityReport {
  file: string;
  entityName: string | null;
  /** Rows in the export */
  rows: number;
  /** Rows kept for the new company */
  kept: number;
  /** Rows of other companies (or shared scope) left out: company → rows ("" = no company) */
  dropped: Record<string, number>;
  /** Company cells changed, per column */
  replaced: Record<string, number>;
  /** Other cells that contain the source company ID: column → example values */
  mentions: Record<string, string[]>;
  /** Record IDs and template-only references cleared, with the number of cells */
  cleared: Record<string, number>;
  /** Values renamed from the template company ID to the new one (formats, codes), per column */
  renamed: Record<string, number>;
  /** Cells set for the new company (approval off, ledger description), per column */
  set: Record<string, number>;
  /** Journal names whose workflow link was cleared: relink after importing the workflows */
  relink: string[];
}

export interface RetargetOptions {
  to: string;
  /** Template company; read from the package when blank */
  from?: string;
  /** Rename the template company ID inside number sequence codes, names and formats (default on) */
  renameInValues?: boolean;
  /** Description for the new company's ledger (the export carries the template's) */
  ledgerDescription?: string;
  /**
   * Start every number sequence at its smallest number (default on), like Copy into legal entity's
   * "reset to smallest": the export carries the template company's current counters.
   */
  resetNumberSequences?: boolean;
  /**
   * Switch budget control off on the new company's ledger (default on): the template's budget
   * control configuration doesn't come with the ledger, and without it posting can be blocked.
   */
  disableBudgetControl?: boolean;
}

export interface RetargetResult {
  zip: Uint8Array;
  from: string;
  to: string;
  entities: RetargetEntityReport[];
  /** Files left untouched because the format isn't CSV or Excel */
  skipped: string[];
}

type TextEncoding = 'utf-16le' | 'utf-8';

function decodeText(bytes: Uint8Array): { text: string; encoding: TextEncoding; bom: boolean } {
  if (bytes[0] === 0xff && bytes[1] === 0xfe)
    return { text: new TextDecoder('utf-16le').decode(bytes.subarray(2)), encoding: 'utf-16le', bom: true };
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
    return { text: new TextDecoder('utf-8').decode(bytes.subarray(3)), encoding: 'utf-8', bom: true };
  if (bytes.length > 1 && bytes[1] === 0x00)
    return { text: new TextDecoder('utf-16le').decode(bytes), encoding: 'utf-16le', bom: false };
  return { text: new TextDecoder('utf-8').decode(bytes), encoding: 'utf-8', bom: false };
}

function encodeText(text: string, encoding: TextEncoding, bom: boolean): Uint8Array {
  if (encoding === 'utf-8') {
    const body = new TextEncoder().encode(text);
    return bom ? new Uint8Array([0xef, 0xbb, 0xbf, ...body]) : body;
  }
  const offset = bom ? 2 : 0;
  const out = new Uint8Array(offset + text.length * 2);
  if (bom) out.set([0xff, 0xfe]);
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    out[offset + i * 2] = c & 0xff;
    out[offset + i * 2 + 1] = c >> 8;
  }
  return out;
}

/** Entity name per file, from Manifest.xml (InputFilePath or SourceFilePath → EntityName). */
function manifestEntities(manifest: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const block of manifest.match(
    /<DataManagementPackageEntityData>[\s\S]*?<\/DataManagementPackageEntityData>/g,
  ) ?? []) {
    const name = /<EntityName>([^<]*)<\/EntityName>/.exec(block)?.[1];
    const file =
      /<InputFilePath>([^<]+)<\/InputFilePath>/.exec(block)?.[1] ??
      /<SourceFilePath>([^<]+)<\/SourceFilePath>/.exec(block)?.[1];
    if (name && file) map.set(file.replace(/^.*[\\/]/, '').toLowerCase(), name.replace(/&amp;/g, '&'));
  }
  return map;
}

/** Indexes of the columns that name a company, for this entity. */
function companyIndexes(header: readonly string[], entityName: string | null): number[] {
  const extra = new Set(ENTITY_COMPANY_COLUMNS[(entityName ?? '').toLowerCase()] ?? []);
  return header.flatMap((h, i) => {
    const col = h.trim().toUpperCase();
    return COMPANY_COLUMNS.has(col) || extra.has(col) ? [i] : [];
  });
}

export interface RetargetedTable {
  header: string[];
  body: string[][];
  /** Per kept row, its index in the exported body */
  keptFrom: number[];
  report: Omit<RetargetEntityReport, 'file' | 'entityName'>;
}

/**
 * Retargets one table: keeps the template company's rows (when the entity names companies at
 * all), rewrites their company cells, clears record IDs and template-only references, and reports
 * other mentions of the template company. Also used on stored template profiles
 * (buildTemplateLedgerPackage), so a generated package follows the same rules.
 */
export function retargetTable(
  table: string[][],
  from: string,
  to: string,
  entityName: string | null,
  opts: Pick<
    RetargetOptions,
    'renameInValues' | 'ledgerDescription' | 'resetNumberSequences' | 'disableBudgetControl'
  > = {},
): RetargetedTable {
  const [header = [], ...rows] = table;
  const report: RetargetedTable['report'] = {
    rows: rows.length,
    kept: 0,
    dropped: {},
    replaced: {},
    mentions: {},
    cleared: {},
    renamed: {},
    set: {},
    relink: [],
  };
  const names = header.map((h) => h.trim().toUpperCase());
  const entity = (entityName ?? '').toLowerCase();
  const renameCols = new Set(opts.renameInValues === false ? [] : (ENTITY_RENAME_COLUMNS[entity] ?? []));
  const target = to.toUpperCase();
  const companyIdx = companyIndexes(header, entityName);
  const clearCols = new Set(ENTITY_CLEAR_COLUMNS[(entityName ?? '').toLowerCase()] ?? []);
  const source = from.toLowerCase();
  // The ID as a whole token: 1100_GJ and "GJ 1100" count, account 110000 doesn't.
  const token = source.replace(/[^a-z0-9]/g, '');
  const mentionsSource = new RegExp(`(^|[^a-z0-9])${token}([^a-z0-9]|$)`, 'i');
  const renameSource = new RegExp(`(^|[^a-z0-9])${token}(?=[^a-z0-9]|$)`, 'gi');
  const companiesOf = (row: string[]): string[] =>
    companyIdx.map((i) => companyPart(row[i] ?? '').toLowerCase()).filter(Boolean);
  // Only an entity that names another company (an every-company export: Ledger, number sequences)
  // is cut to the template company's rows; there a blank company is a shared row. In an entity
  // exported from the template company alone (journal names), every row is the template's, blank
  // company or not.
  const filters = rows.some((r) => companiesOf(r).some((c) => c !== source));
  const body: string[][] = [];
  const keptFrom: number[] = [];
  rows.forEach((original, n) => {
    const companies = companiesOf(original);
    if (filters && !companies.includes(source)) {
      const key = companies[0] ?? '';
      report.dropped[key] = (report.dropped[key] ?? 0) + 1;
      return;
    }
    const row = [...original];
    names.forEach((col, i) => {
      const value = row[i] ?? '';
      if (!value) return;
      if (isRecIdColumn(col) || clearCols.has(col)) {
        row[i] = '';
        report.cleared[col] = (report.cleared[col] ?? 0) + 1;
      } else if (companyIdx.includes(i) && companyPart(value).toLowerCase() === source) {
        const part = companyPart(value);
        // Lower case only where the export used lower-case letters (dataAreaId); upper case otherwise.
        const id = /[a-z]/.test(part) && part === part.toLowerCase() ? to.toLowerCase() : to.toUpperCase();
        const rest = value.includes(SCOPE_SEPARATOR) ? value.slice(value.indexOf(SCOPE_SEPARATOR)) : '';
        row[i] = id + rest;
        report.replaced[col] = (report.replaced[col] ?? 0) + 1;
      } else if (renameCols.has(col) && mentionsSource.test(value)) {
        row[i] = value.replace(renameSource, `$1${target}`);
        report.renamed[col] = (report.renamed[col] ?? 0) + 1;
      } else if (mentionsSource.test(value)) {
        const list = (report.mentions[col] ??= []);
        if (list.length < 3 && !list.includes(value)) list.push(value);
      }
    });
    const set = (col: string, value: string): void => {
      const i = names.indexOf(col);
      if (i < 0 || row[i] === value) return;
      row[i] = value;
      report.set[col] = (report.set[col] ?? 0) + 1;
    };
    // Every journal name imports with the Approval workflow box off: the new company has no
    // workflows yet. Those that had a workflow or approval on are listed to relink and switch back on.
    if (entity === 'journal names') {
      const at = (col: string): string => original[names.indexOf(col)] ?? '';
      const hadWorkflow = at('WORKFLOWID') !== '' || at('ISWORKFLOWAPPROVALACTIVE') === 'Yes';
      set('ISWORKFLOWAPPROVALACTIVE', 'No');
      if (hadWorkflow) report.relink.push(row[names.indexOf('NAME')] ?? '');
    }
    if (entity === 'ledger' && opts.ledgerDescription?.trim())
      set('DESCRIPTION', opts.ledgerDescription.trim());
    if (entity === 'ledger' && opts.disableBudgetControl !== false) set('ISBUDGETCONTROLENABLED', 'No');
    const smallest = row[names.indexOf('SMALLEST')];
    if (entity === 'number sequence code v2' && opts.resetNumberSequences !== false && smallest)
      set('NEXT', smallest);
    body.push(row);
    keptFrom.push(n);
  });
  report.kept = body.length;
  return { header, body, keptFrom, report };
}

/** First worksheet as text rows (dates as yyyy-mm-dd, like the CSV export). */
function sheetTable(ws: ExcelJS.Worksheet | undefined): string[][] {
  const table: string[][] = [];
  ws?.eachRow({ includeEmpty: true }, (row, n) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : [];
    table[n - 1] = values.map((v) => String(cellText(v as ExcelJS.CellValue) ?? ''));
  });
  return Array.from(table, (r) => r ?? []);
}

async function loadSheet(data: Uint8Array): Promise<ExcelJS.Workbook> {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength) as ArrayBuffer);
  return wb;
}

async function readTable(name: string, data: Uint8Array): Promise<string[][]> {
  return /\.csv$/i.test(name)
    ? parseCsv(decodeText(data).text)
    : sheetTable((await loadSheet(data)).worksheets[0]);
}

/**
 * The company the package was exported from: the one company named by the entities that name only
 * one (journal names, journal control, …). Entities like Ledger or number sequences export every
 * company, so counting across them would pick whichever company has the most rows.
 */
async function sourceCompany(zip: JSZip, entities: Map<string, string>): Promise<string | null> {
  const votes = new Map<string, number>();
  for (const file of Object.values(zip.files)) {
    if (file.dir || !/\.(csv|xlsx)$/i.test(file.name)) continue;
    const entityName = entities.get(file.name.replace(/^.*\//, '').toLowerCase()) ?? null;
    const [header = [], ...rows] = await readTable(file.name, await file.async('uint8array'));
    const idx = companyIndexes(header, entityName);
    const seen = new Set(
      rows.flatMap((r) => idx.map((i) => companyPart(r[i] ?? '').toLowerCase()).filter(Boolean)),
    );
    const only = seen.size === 1 ? [...seen][0] : undefined;
    if (only) votes.set(only, (votes.get(only) ?? 0) + 1);
  }
  return [...votes.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

export async function retargetDataPackage(bytes: Uint8Array, opts: RetargetOptions): Promise<RetargetResult> {
  const to = opts.to.trim();
  if (!/^[A-Za-z0-9]{1,4}$/.test(to))
    throw new Error(`"${opts.to}" is not a D365 company ID (1–4 letters or digits)`);
  let zip: JSZip;
  try {
    zip = await JSZip.loadAsync(bytes);
  } catch {
    throw new Error('The file is not a .zip data package');
  }
  const manifestFile = Object.values(zip.files).find((f) => /(^|\/)Manifest\.xml$/i.test(f.name));
  if (!manifestFile) throw new Error('No Manifest.xml: export the data project with format "Package"');
  // D365 writes the manifest in UTF-16 with a BOM.
  const entities = manifestEntities(decodeText(await manifestFile.async('uint8array')).text);
  const from = (opts.from?.trim() || (await sourceCompany(zip, entities)) || '').toLowerCase();
  if (!from)
    throw new Error(
      'Could not tell which company the package was exported from; enter the template company ID',
    );

  const out = new JSZip();
  const report: RetargetEntityReport[] = [];
  const skipped: string[] = [];
  const date = new Date('2000-01-01T00:00:00Z');
  for (const file of Object.values(zip.files)) {
    if (file.dir) continue;
    const data = await file.async('uint8array');
    const base = file.name.replace(/^.*\//, '');
    const entityName = entities.get(base.toLowerCase()) ?? null;
    if (/\.csv$/i.test(file.name)) {
      const { text, encoding, bom } = decodeText(data);
      const r = retargetTable(parseCsv(text), from, to, entityName, opts);
      out.file(file.name, encodeText(toCsv(r.header, r.body), encoding, bom), { date });
      report.push({ file: base, entityName, ...r.report });
    } else if (/\.xlsx$/i.test(file.name)) {
      const wb = await loadSheet(data);
      const ws = wb.worksheets[0];
      const table = sheetTable(ws);
      const r = retargetTable(table, from, to, entityName, opts);
      // A fresh sheet with the header and the kept rows. Unchanged cells keep their original
      // Excel value (dates, numbers); changed cells get the new text.
      const fresh = new ExcelJS.Workbook();
      const sheet = fresh.addWorksheet(ws?.name ?? 'Sheet1');
      sheet.addRow(r.header);
      r.body.forEach((values, k) => {
        const n = r.keptFrom[k] ?? 0;
        const sourceRow = ws?.getRow(n + 2);
        const before = table[n + 1] ?? [];
        sheet.addRow(
          values.map((v, j) =>
            v === before[j] ? (sourceRow?.getCell(j + 1).value ?? null) : v === '' ? null : v,
          ),
        );
      });
      out.file(file.name, new Uint8Array((await fresh.xlsx.writeBuffer()) as ArrayBuffer), { date });
      report.push({ file: base, entityName, ...r.report });
    } else {
      out.file(file.name, data, { date });
      if (!/\.xml$/i.test(file.name)) skipped.push(base);
    }
  }
  return {
    zip: await out.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }),
    from,
    to: to.toUpperCase(),
    entities: report,
    skipped,
  };
}

// ── extra entity files ───────────────────────────────────────────────────────

export interface EntityFile {
  name: string;
  bytes: Uint8Array;
}

export interface MergeResult {
  zip: Uint8Array;
  /** Entities that were in the manifest and got the new file */
  replaced: string[];
  /** Entities added to the manifest (after the existing ones, in the order given) */
  added: string[];
  /** Files that didn't match a known entity, left out */
  unknown: string[];
}

const normal = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The entity a file is for, from its name: an export named "copy-company-export-Journal names..xlsx"
 * is Journal names. The longest matching entity name wins (Journal Control V2 over Journal …).
 */
function entityForFile(fileName: string, candidates: readonly string[]): string | null {
  const base = normal(fileName.replace(/^.*[\\/]/, '').replace(/\.(xlsx|csv)$/i, ''));
  return (
    [...candidates]
      .filter((name) => base.includes(normal(name)))
      .sort((a, b) => normal(b).length - normal(a).length)[0] ?? null
  );
}

/**
 * Adds single-entity exports (Excel or CSV, from Data management → Export of one entity) to a
 * package exported earlier: a file for an entity already in the manifest replaces its data file;
 * an entity not in it gets a manifest entry, mapped column for column, after the existing ones.
 * The manifest keeps its encoding.
 */
export async function addEntityFiles(
  packageBytes: Uint8Array,
  files: readonly EntityFile[],
): Promise<MergeResult> {
  const zip = await JSZip.loadAsync(packageBytes);
  const manifestFile = Object.values(zip.files).find((f) => /(^|\/)Manifest\.xml$/i.test(f.name));
  if (!manifestFile) throw new Error('No Manifest.xml: export the data project with format "Package"');
  const decoded = decodeText(await manifestFile.async('uint8array'));
  let manifest = decoded.text;
  const inManifest = manifestEntities(manifest);
  const fileByEntity = new Map([...inManifest].map(([file, entity]) => [entity.toLowerCase(), file]));
  const known = [...new Set([...inManifest.values(), ...LEDGER_EXPORT_ENTITIES.map((e) => e.entityName)])];
  let sequence = Math.max(
    0,
    ...[...manifest.matchAll(/<SequenceInLevel>(\d+)<\/SequenceInLevel>/g)].map((m) => Number(m[1])),
  );
  const result: Omit<MergeResult, 'zip'> = { replaced: [], added: [], unknown: [] };

  for (const file of files) {
    const entityName = entityForFile(file.name, known);
    if (!entityName) {
      result.unknown.push(file.name);
      continue;
    }
    const existing = fileByEntity.get(entityName.toLowerCase());
    const ext = /\.csv$/i.test(file.name) ? '.csv' : '.xlsx';
    if (existing) {
      const path =
        Object.keys(zip.files).find((p) => p.replace(/^.*\//, '').toLowerCase() === existing) ?? existing;
      zip.file(path, file.bytes);
      result.replaced.push(entityName);
      continue;
    }
    const [header = []] = await readTable(file.name, file.bytes);
    const inputFilePath = `${entityName}${ext}`;
    sequence += 1;
    const block = manifestEntityXml({
      entityName,
      columns: header.filter(Boolean),
      inputFilePath,
      sequence,
      sourceFormat: ext === '.csv' ? 'CSV-Unicode' : 'excel',
      targetEntity: LEDGER_EXPORT_ENTITIES.find((e) => e.entityName === entityName)?.targetEntity,
    });
    manifest = manifest.replace('</PackageEntityList>', `${block}</PackageEntityList>`);
    zip.file(inputFilePath, file.bytes);
    fileByEntity.set(entityName.toLowerCase(), inputFilePath.toLowerCase());
    result.added.push(entityName);
  }
  zip.file(manifestFile.name, encodeText(manifest, decoded.encoding, decoded.bom));
  return { zip: await zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' }), ...result };
}

/** "LG-LEDGER-EXPORT.zip" → "LG-LEDGER-EXPORT - RSM1.zip" */
export const retargetedPackageName = (original: string, to: string): string =>
  `${original.replace(/^.*[\\/]/, '').replace(/\.zip$/i, '')} - ${to.toUpperCase()}.zip`;
